import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { GraduationCap, Calendar, Award, BookOpen, Code, Lightbulb, Check } from 'lucide-react';

const Education: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const learningAreas = [
    {
      icon: Code,
      title: "Programming & Development",
      description: "Comprehensive training in software development, programming languages, and system design",
      color: "bg-blue-600"
    },
    {
      icon: BookOpen,
      title: "Computer Science Fundamentals",
      description: "Core concepts in algorithms, data structures, database management, and computer systems",
      color: "bg-purple-600"
    },
    {
      icon: Lightbulb,
      title: "Problem Solving & Logic",
      description: "Analytical thinking, logical reasoning, and systematic approach to complex challenges",
      color: "bg-green-600"
    }
  ];

  const achievements = [
    "Completed intensive Computer Science curriculum",
    "Gained hands-on experience with multiple programming languages",
    "Developed strong foundation in software engineering principles",
    "Built practical skills that led to immediate career opportunities"
  ];

  return (
    <section id="education" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={stagger}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-amber-100 text-amber-700 border border-amber-200 mb-4"
            >
              <Check/> Academic Background
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Education & <span className="text-amber-600">Learning</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Building a solid foundation in computer science that launched a successful 
              career in software development and technology innovation.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Education Card */}
            <motion.div variants={fadeInUp}>
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl rounded-3xl overflow-hidden">
                <CardContent className="p-0">
                  {/* Header */}
                  <div className="bg-amber-600 p-8 text-white">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <GraduationCap className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold mb-1">Diploma in Computer Science</h3>
                        <p className="text-orange-100 font-medium">AMA Computer Learning Center</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm text-orange-100">
                      <Calendar className="h-4 w-4" />
                      <span>2008 – 2010</span>
                    </div>
                  </div>
                  
                  {/* Body */}
                  <div className="p-8">
                    <div className="mb-6">
                      <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <BookOpen className="h-5 w-5 mr-2 text-orange-500" />
                        Program Highlights
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        Comprehensive computer science program focusing on programming fundamentals, 
                        software development methodologies, and practical application development. 
                        This education provided the technical foundation that enabled immediate 
                        entry into professional software development roles.
                      </p>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                        <Award className="h-5 w-5 mr-2 text-orange-500" />
                        Key Achievements
                      </h4>
                      <ul className="space-y-2">
                        {achievements.map((achievement, index) => (
                          <li key={index} className="flex items-start space-x-3 text-gray-700">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="leading-relaxed">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Learning Areas */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-8">
                Areas of Study
              </h4>
              
              {learningAreas.map((area, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="group"
                >
                  <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-xl ${area.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          <area.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h5 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                            {area.title}
                          </h5>
                          <p className="text-gray-600 leading-relaxed">
                            {area.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Bottom Section - Continuous Learning */}
          <motion.div
            variants={fadeInUp}
            className="mt-16 text-center p-8 bg-amber-50 rounded-3xl border border-amber-200"
          >
            <div className="max-w-4xl mx-auto">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Commitment to Continuous Learning
              </h4>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                While formal education provided the foundation, the journey in technology never stops. 
                I continuously expand my skills through practical experience, online courses, 
                and staying current with emerging technologies and industry best practices.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                {[
                  { number: "5+", label: "Years of Learning" },
                  // { number: "15+", label: "Technologies Mastered" },
                  // { number: "100+", label: "Projects Completed" }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    className="text-center p-4 bg-white/60 rounded-2xl backdrop-blur-sm"
                  >
                    <div className="text-3xl font-bold text-amber-600 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Education;